import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, MapPin, GraduationCap } from "lucide-react";

export default function Login() {
  const { user, loading, setUser } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate("/");
  }, [user, loading, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.post("/auth/login", { email, password });
      setUser(res.data);
      navigate("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left — brand / form */}
      <div className="flex flex-col justify-center px-8 sm:px-16 lg:px-20 py-10 bg-background overflow-y-auto">
        <div className="max-w-md w-full mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <div className="h-11 w-11 rounded-xl bg-neutral-900 flex items-center justify-center p-1">
              <img src="/hv-logo.png" alt="Hundeverket" className="w-full h-full object-contain" />
            </div>
            <div>
              <div className="font-heading font-bold text-2xl leading-none tracking-tight">Hundeverket</div>
              <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground mt-1">Franchise OS for dog training</div>
            </div>
          </div>

          <h1 className="font-heading text-4xl sm:text-5xl tracking-tight font-semibold text-foreground">
            The operating system for modern dog training brands.
          </h1>
          <p className="text-muted-foreground mt-3 text-base leading-relaxed">
            Manage franchise locations, trainers, clients, dogs and training journeys — all in one professional platform.
          </p>

          <form onSubmit={handleLogin} className="mt-8 space-y-3">
            <div className="grid gap-1.5">
              <Label className="text-sm">Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@hundeverket.com" required data-testid="login-email" />
            </div>
            <div className="grid gap-1.5">
              <Label className="text-sm">Password</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required data-testid="login-password" />
            </div>
            <Button type="submit" size="lg" className="w-full h-11 text-base" disabled={busy} data-testid="login-submit">
              {busy ? "Signing in..." : "Sign in"}
            </Button>
          </form>

          <div className="grid grid-cols-3 gap-4 mt-8">
            {[
              { icon: MapPin, t: "Locations" },
              { icon: GraduationCap, t: "Programs" },
              { icon: ShieldCheck, t: "Role-based" },
            ].map((f) => (
              <div key={f.t} className="text-center">
                <div className="h-10 w-10 rounded-lg bg-secondary flex items-center justify-center mx-auto mb-2">
                  <f.icon size={18} className="text-primary" strokeWidth={1.5} />
                </div>
                <div className="text-xs text-muted-foreground font-medium">{f.t}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right — hero image */}
      <div className="hidden lg:block relative">
        <img
          src="https://images.unsplash.com/photo-1620289052446-202137ffa876"
          alt="Dog trainer"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-neutral-900/45" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <div className="text-2xl font-heading font-semibold leading-snug">
            "A serious platform for a serious craft."
          </div>
          <div className="text-white/80 mt-2 text-sm">Head Office · Locations · Trainers · Dogs</div>
        </div>
      </div>
    </div>
  );
}
